// Version check utility to force reload when new version is deployed
// This prevents users from running stale cached versions

const APP_VERSION = import.meta.env.VITE_APP_VERSION || '1.0.1';
const VERSION_CHECK_KEY = 'app_version_check';
const LAST_VERSION_KEY = 'app_last_version';

interface VersionCheck {
  lastChecked: number;
  lastVersion: string;
}

export const checkForNewVersion = async (): Promise<boolean> => {
  try {
    // Check every 30 seconds max
    const stored = localStorage.getItem(VERSION_CHECK_KEY);
    const now = Date.now();
    
    if (stored) {
      const check: VersionCheck = JSON.parse(stored);
      if (now - check.lastChecked < 30000) {
        return false;
      }
    }

    // Fetch version.json with cache-busting
    const response = await fetch(`/version.json?t=${now}`, {
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    });

    if (!response.ok) {
      console.warn('[Version Check] Failed to fetch version:', response.status);
      return false;
    }

    const data = await response.json();
    const serverVersion = data.version;
    const buildTime = data.buildTime;

    console.log('[Version Check] Current:', APP_VERSION, 'Server:', serverVersion, 'Build:', new Date(buildTime).toISOString());

    // Update check time
    localStorage.setItem(VERSION_CHECK_KEY, JSON.stringify({
      lastChecked: now,
      lastVersion: serverVersion
    }));

    // Get stored version
    const lastVersion = localStorage.getItem(LAST_VERSION_KEY);
    
    // If version changed, we need to reload
    if (lastVersion && lastVersion !== serverVersion) {
      console.log('[Version Check] New version detected! Updating from', lastVersion, 'to', serverVersion);
      return true;
    }

    // Store current version
    localStorage.setItem(LAST_VERSION_KEY, serverVersion);
    return false;
  } catch (error) {
    console.error('[Version Check] Error:', error);
    return false;
  }
};

export const forceReload = () => {
  console.log('[Version Check] Forcing reload...');
  
  // Clear all caches
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then(registrations => {
      registrations.forEach(registration => {
        registration.unregister();
      });
    });
  }

  if ('caches' in window) {
    caches.keys().then(names => {
      names.forEach(name => {
        caches.delete(name);
      });
    });
  }

  // Force hard reload
  window.location.reload();
};

export const initVersionCheck = () => {
  console.log('[Version Check] Initializing version', APP_VERSION);
  
  // Check on init
  checkForNewVersion().then(shouldReload => {
    if (shouldReload) {
      forceReload();
    }
  });

  // Check every 2 minutes
  setInterval(async () => {
    const shouldReload = await checkForNewVersion();
    if (shouldReload) {
      forceReload();
    }
  }, 120000);

  // Check on visibility change
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      checkForNewVersion().then(shouldReload => {
        if (shouldReload) {
          forceReload();
        }
      });
    }
  });
};
